import { getSecondary } from '@brands/Utils/getPrimaryColor';
import { Box, Checkbox, Chip, FormControl, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import cn from 'classnames';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  options: { id: string; value: string | number; label: string }[];
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  name: string;
  label?: string;
  onChange?: (value: string[]) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  value?: (string | { id: string; value: string | number; label: string })[];
  required?: boolean;
  hideLabel?: boolean;
  styledTag?: boolean;
  displayCheckbox?: boolean;
  freeSolo?: boolean;
}

const MultiSelectInputFreeSolo = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      options,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      label,
      onChange,
      onBlur,
      disabled = false,
      value = [],
      required = false,
      hideLabel = false,
      styledTag = false,
      displayCheckbox = true,
      freeSolo = true,
      ...props
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState('');

    const hiddenOptionValues = ['D9995', 'D9996'];

    useEffect(() => {
      if (errors && name) {
        const nameArray = name.split('.');
        const error = nameArray.length > 1 ? errors[nameArray[0]]?.[nameArray[1]]?.message : errors[name]?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    const selectedValues = useMemo(
      () =>
        value
          .map((v) => (typeof v === 'string' ? v.trim() : v?.label?.toString().trim()))
          .filter((v): v is string => !!v),
      [value],
    );

    const availableOptions = useMemo(
      () =>
        options.filter((opt) => {
          const optionValue = String(opt.value).trim();
          const optionLabel = opt.label.toString().trim();

          const isHidden = hiddenOptionValues.includes(optionValue) || hiddenOptionValues.includes(optionLabel);
          const isAlreadySelected = selectedValues.includes(optionLabel);

          return !isHidden && !isAlreadySelected;
        }),
      [options, selectedValues],
    );

    return (
      <Box className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle}>
        <FormControl
          sx={{
            border: '0',
            width: '100%',
            height: 'fit-content',
            minHeight: '56px',
            '& .MuiAutocomplete-root': {
              height: 'fit-content',
            },
          }}
          error={!!errorMessage}
        >
          <Autocomplete
            multiple
            freeSolo={freeSolo}
            disablePortal
            forcePopupIcon
            popupIcon={<ArrowDropDownIcon />}
            options={availableOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            fullWidth
            value={value.filter((val) => (typeof val === 'string' ? val.trim() !== '' : !!val?.label))}
            onChange={(event, newValue) => {
              const mapped = newValue
                .map((item) => (typeof item === 'string' ? item.trim() : item.label?.toString().trim()))
                .filter((v): v is string => !!v);
              onChange?.(mapped);
            }}
            disabled={disabled}
            ref={ref}
            {...props}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => {
                const lbl = typeof option === 'string' ? option : option.label;
                return styledTag ? (
                  <Chip
                    variant="outlined"
                    color="primary"
                    label={lbl}
                    {...getTagProps({ index })}
                    key={lbl}
                    className="fs-exclude"
                    sx={{
                      borderRadius: '24px',
                      fontWeight: 500,
                      color: getSecondary(),
                      borderColor: getSecondary(),
                      '.MuiChip-deleteIcon': {
                        color: getSecondary(),
                      },
                      m: '4px 6px 4px 0',
                    }}
                  />
                ) : (
                  lbl
                );
              })
            }
            renderOption={(optionProps, option, { selected }) => {
              const lbl = typeof option === 'string' ? option : option.label;
              return (
                <li {...optionProps} key={lbl} className={cn(optionProps.className, 'fs-exclude')}>
                  {displayCheckbox && <Checkbox checked={selected} size="small" />}
                  {lbl}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className={cn(styles.input, inputClass)}
                label={hideLabel ? undefined : label}
                InputLabelProps={{
                  className: 'fs-unmask',
                  required: !hideLabel && Boolean(required),
                  shrink: hideLabel ? false : Boolean(value?.length) || undefined,
                  sx: !hideLabel
                    ? {
                        fontSize: '14px',
                        color: 'rgba(23, 36, 51, 0.6)',
                        backgroundColor: 'white',
                        px: 0.3,
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(6px, -10px) scale(0.75)',
                        },
                        '& .MuiFormLabel-asterisk': {
                          color: '#d32f2f',
                          opacity: 1,
                          fontWeight: 700,
                        },
                      }
                    : undefined,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{params.InputProps.endAdornment}</>,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 'fit-content',
                    minHeight: '56px',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    padding: '8px 14px',
                    '& .MuiAutocomplete-inputRoot': {
                      padding: '4px 0',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '8px',
                  },
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                    transition: 'max-width 0.2s ease-out',
                  },
                  '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },
                }}
                inputProps={{
                  ...params.inputProps,
                  'aria-required': required || undefined,
                  'data-testid': `multi-select-input-text-${name}`,
                  className: cn(params.inputProps.className, 'fs-exclude'),
                }}
                error={!!errorMessage}
                helperText={errorMessage}
                {...(register ? register(name, registerOptions) : {})}
              />
            )}
          />
        </FormControl>
      </Box>
    );
  },
);

export default MultiSelectInputFreeSolo;
